/**
 * The boat drawn at its real size, in its real place.
 *
 * A plotter symbol is a marker: it sits at the GNSS antenna and stays the same
 * size however far you zoom in. That is right at passage scale and wrong when
 * you are placing an anchor or edging into a berth, because at that point the
 * questions are "where is my bow" and "will my stern clear that", and a fixed
 * arrowhead answers neither.
 *
 * Two things have to be known for this to be honest, and both come from the
 * boat, not from us:
 *
 *  - the hull's size, length and beam;
 *  - where the ANTENNA sits on it. The fix is the antenna's position, not the
 *    boat's. On a yacht with the aerial at the stern rail that is most of the
 *    boat's length away from the bow, and it is the bow that touches things.
 *
 * This is the consistent common reference point that IEC-compliant ECDIS is
 * required to handle, for exactly the reason above. With the offsets set, the
 * anchor drops where it actually leaves the bow roller, and the swinging circle
 * is centred there rather than several metres aft of it.
 *
 * Pure geometry, no MapLibre and no stores, so every case here is testable.
 */

/** Metres per degree of latitude. Close enough over a boat-length. */
const M_PER_DEG_LAT = 111_320;

export interface HullInput {
  /** The GNSS antenna's position, which is what the fix actually gives us. */
  position: [number, number];
  /** Which way the bow points, degrees from true north. */
  headingDeg: number;
  /** Overall length, metres. */
  lengthM: number;
  /** Maximum beam, metres. */
  beamM: number;
  /** Antenna's distance AFT of the bow, along the centreline, metres. */
  fromBowM: number;
  /**
   * Antenna's offset from the centreline, metres, positive to STARBOARD.
   * Usually 0 or close to it; a mast-top or pushpit aerial can be off to one
   * side by enough to matter at berthing scale.
   */
  fromCentreM?: number;
}

/**
 * A point in the boat's own frame: x to starboard, y forward, metres from the
 * antenna. Exported because the hull shape below is easier to read this way and
 * easier still to test.
 */
export interface BoatPoint {
  x: number;
  y: number;
}

/** Offset a lon/lat by metres east and north. */
export function offsetMetres(
  position: [number, number],
  eastM: number,
  northM: number,
): [number, number] {
  const [lon, lat] = position;
  const dLat = northM / M_PER_DEG_LAT;
  // Longitude degrees shrink with latitude; guard the poles so we never divide
  // by ~0 and fling the hull across the world.
  const cos = Math.max(0.01, Math.cos((lat * Math.PI) / 180));
  const dLon = eastM / (M_PER_DEG_LAT * cos);
  return [lon + dLon, lat + dLat];
}

/**
 * Turn a point in the boat's frame into lon/lat, given the heading. Forward is
 * along the heading; starboard is 90 degrees clockwise of it.
 */
export function boatPointToLonLat(
  position: [number, number],
  headingDeg: number,
  p: BoatPoint,
): [number, number] {
  const t = (headingDeg * Math.PI) / 180;
  const sin = Math.sin(t);
  const cos = Math.cos(t);
  // Forward unit vector is (sin, cos) in (east, north); starboard is (cos, -sin).
  const eastM = p.y * sin + p.x * cos;
  const northM = p.y * cos - p.x * sin;
  return offsetMetres(position, eastM, northM);
}

/** Whether we know enough about the boat to draw it rather than mark it. */
export function canDrawHull(input: Partial<HullInput> | null | undefined): boolean {
  if (!input) return false;
  const { lengthM, beamM, fromBowM } = input;
  if (!isPositive(lengthM) || !isPositive(beamM)) return false;
  // The antenna has to be somewhere ON the boat. A figure longer than the boat
  // is a typo, and drawing a hull from it would put the bow in the wrong place
  // with total confidence.
  if (fromBowM == null || !Number.isFinite(fromBowM) || fromBowM < 0) return false;
  return fromBowM <= (lengthM as number);
}

function isPositive(v: number | null | undefined): v is number {
  return v != null && Number.isFinite(v) && v > 0;
}

/**
 * The hull outline in the boat's own frame, bow first and going clockwise
 * (starboard side down to the transom, then back up the port side).
 *
 * The shape is a plan-view generalisation rather than any particular hull: a
 * point at the bow, maximum beam about a third aft of it, and a transom a touch
 * narrower than the beam. It reads as a boat from above, which is all a chart
 * symbol needs to do, and it is deliberately not a fake of any real design.
 */
export function hullPoints(input: HullInput): BoatPoint[] {
  const { lengthM, beamM, fromBowM } = input;
  const offCentre = input.fromCentreM ?? 0;
  // The centreline sits opposite the antenna's own offset from it.
  const x0 = -offCentre;
  const bow = fromBowM;
  const stern = lengthM - fromBowM;
  const halfBeam = beamM / 2;
  const shoulderY = bow - lengthM * 0.3;
  const transomHalf = halfBeam * 0.78;
  return [
    { x: x0, y: bow },
    { x: x0 + halfBeam, y: shoulderY },
    { x: x0 + transomHalf, y: -stern },
    { x: x0 - transomHalf, y: -stern },
    { x: x0 - halfBeam, y: shoulderY },
  ];
}

/**
 * The hull as a closed ring of lon/lat, ready for a GeoJSON polygon. Null when
 * we do not know enough about the boat, so a caller falls back to the symbol
 * rather than drawing an invented shape.
 */
export function hullRing(input: HullInput): [number, number][] | null {
  if (!canDrawHull(input)) return null;
  const ring = hullPoints(input).map((p) => boatPointToLonLat(input.position, input.headingDeg, p));
  // GeoJSON polygons close explicitly.
  return [...ring, ring[0]];
}

/**
 * Where the bow actually is: the point the anchor leaves the boat from, and the
 * point a swinging circle should be centred on.
 *
 * Falls back to the antenna position when the offset is not set, which is the
 * behaviour anchoring had before any of this existed, so nothing silently
 * changes for a boat whose measurements have not been entered.
 */
export function bowPosition(input: {
  position: [number, number];
  headingDeg: number;
  fromBowM?: number | null;
  fromCentreM?: number | null;
}): [number, number] {
  const d = input.fromBowM;
  if (d == null || !Number.isFinite(d) || d <= 0) return input.position;
  return boatPointToLonLat(input.position, input.headingDeg, {
    x: -(input.fromCentreM ?? 0),
    y: d,
  });
}

/**
 * Whether the hull is worth drawing at this scale: swap the moment the real
 * boat is bigger than the symbol would be, so the change reads as one boat
 * growing rather than two different marks (fb-mscxlrg4-0). The arrow glyph is
 * about 18 px across at its usual size, so that is the crossover; below it the
 * hull is a smudge and the arrowhead says more.
 */
export function hullWorthDrawing(lengthM: number, metresPerPixel: number, minPx = 18): boolean {
  if (!isPositive(lengthM) || !isPositive(metresPerPixel)) return false;
  return lengthM / metresPerPixel >= minPx;
}

/**
 * Where to drop the anchor from the boat's CURRENT state: the bow if we know
 * where it is, otherwise the fix. One helper so the offset cannot end up
 * applied at three of the five places an anchor can be dropped.
 */
export function anchorDropPosition(input: {
  position: [number, number];
  headingDeg: number;
  gnssFromBowM?: number | null;
  gnssFromCentreM?: number | null;
}): [number, number] {
  return bowPosition({
    position: input.position,
    headingDeg: input.headingDeg,
    fromBowM: input.gnssFromBowM,
    fromCentreM: input.gnssFromCentreM,
  });
}

/**
 * Where the anchor lies, from a rode length and a bearing taken to it.
 *
 * The other way of recording an anchor: rather than "drop it here", stand at
 * the bow, point a compass at where the chain goes in, and say how much rode is
 * out. That is how you record an anchor you laid twenty minutes ago, and it is
 * the only way to do it at all once the boat has swung off the spot
 * (fb-msca16x7-1).
 *
 * The bearing is TRUE, measured FROM the boat TO the anchor, and the distance
 * is along the surface. A rode length is not a surface distance, because the
 * chain hangs down to the seabed, so a caller with a depth to hand should pass
 * the horizontal run rather than the rode paid out; horizontalRunM does that
 * sum. Getting it wrong puts the anchor further away than it is, which makes
 * the swinging circle too big and the alarm too slow, so it errs the wrong way.
 */
export function anchorFromBearing(
  from: [number, number],
  trueBearingDeg: number,
  distanceM: number,
): [number, number] {
  const t = (trueBearingDeg * Math.PI) / 180;
  return offsetMetres(from, Math.sin(t) * distanceM, Math.cos(t) * distanceM);
}

/**
 * The horizontal distance from the bow to the anchor, given the rode paid out
 * and the depth of water. Pythagoras on a straight rode, which underestimates
 * slightly because a real catenary sags, and underestimating is the safe way
 * round here: it puts the anchor closer, so the swinging circle is smaller and
 * the alarm sounds sooner. Never returns more than the rode.
 */
export function horizontalRunM(rodeM: number, depthM: number): number {
  if (!(rodeM > 0)) return 0;
  const d = Math.max(0, depthM);
  if (d >= rodeM) return 0; // straight up and down, or bad numbers
  return Math.sqrt(rodeM * rodeM - d * d);
}
